<script setup lang="ts">
const route = useRoute()
const { theme, isPro } = useSiteTheme()

const isMenuOpen = ref(false)
const isScrolled = ref(false)

const links = [
    { label: 'Particuliers', to: '/' },
    { label: 'Entreprises', to: '/entreprises' },
]

const contactLink = computed(() => {
    return isPro.value
        ? { path: '/entreprises', hash: '#contact' }
        : { path: '/', hash: '#contact' }
})

const handleScroll = () => {
    isScrolled.value = window.scrollY > 20
}

const toggleMenu = () => {
    isMenuOpen.value = !isMenuOpen.value
}

const closeMenu = () => {
    isMenuOpen.value = false
}

const isActiveLink = (to: string) => {
    if (to === '/') {
        return route.path === '/'
    }

    return route.path.startsWith(to)
}

watch(
    () => route.fullPath,
    () => {
        closeMenu()
    }
)

onMounted(() => {
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
})

onBeforeUnmount(() => {
    window.removeEventListener('scroll', handleScroll)
})
</script>

<template>
    <header :class="[
        'fixed top-0 left-0 z-50 w-full transition-all duration-300',
        isScrolled || isMenuOpen
            ? 'bg-white shadow-sm'
            : 'bg-white lg:bg-transparent'
    ]">
        <div class="container mx-auto px-4">
            <div class="grid min-h-[72px] grid-cols-[auto_1fr_auto] items-center gap-4">
                <NuxtLink to="/" aria-label="Retour à l’accueil" class="relative z-[60] py-4 text-slate-900"
                    @click="closeMenu">
                    <div class="w-[180px]">
                        <TheLogo :key="theme.name" />
                    </div>
                </NuxtLink>

                <nav id="menu-principal" aria-label="Navigation principale" :class="[
                    'absolute left-0 top-full w-full overflow-hidden bg-white transition-all duration-300 md:static md:w-auto md:overflow-visible md:bg-transparent',
                    isMenuOpen
                        ? 'visible max-h-[500px] border-t border-slate-200 opacity-100'
                        : 'invisible max-h-0 opacity-0 md:visible md:max-h-none md:opacity-100'
                ]">
                    <ul
                        class="flex flex-col items-start gap-6 px-4 py-6 sm:px-6 md:flex-row md:items-center md:justify-center md:gap-8 md:px-0 md:py-0">
                        <li v-for="link in links" :key="link.to">
                            <NuxtLink :to="link.to" :class="[
                                'text-base font-medium transition',
                                isActiveLink(link.to)
                                    ? theme.accentText
                                    : 'text-slate-900',
                                isPro
                                    ? 'hover:text-red-700'
                                    : 'hover:text-sky-700'
                            ]" @click="closeMenu">
                                {{ link.label }}
                            </NuxtLink>
                        </li>

                        <li class="w-full pt-2 md:hidden">
                            <NuxtLink :to="contactLink" :class="[
                                'inline-flex w-full items-center justify-center rounded-lg px-5 py-3 text-sm font-semibold text-white transition',
                                theme.buttonPrimary
                            ]" @click="closeMenu">
                                Contactez-nous
                            </NuxtLink>
                        </li>
                    </ul>
                </nav>

                <div class="relative z-[60] flex items-center justify-end gap-3">
                    <NuxtLink :to="contactLink" :class="[
                        'hidden rounded-lg px-5 py-3 text-sm font-semibold text-white transition md:inline-flex',
                        theme.buttonPrimary
                    ]">
                        Contactez-nous
                    </NuxtLink>

                    <button type="button" class="relative flex h-10 w-10 items-center justify-center md:hidden"
                        :aria-label="isMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'" :aria-expanded="isMenuOpen"
                        aria-controls="menu-principal" @click="toggleMenu">
                        <span class="relative block h-5 w-7">
                            <span :class="[
                                'absolute left-0 h-[3px] w-7 rounded-full bg-slate-800 transition-all duration-300',
                                isMenuOpen ? 'top-1/2 -translate-y-1/2 rotate-45' : 'top-0'
                            ]" />

                            <span :class="[
                                'absolute left-0 top-1/2 h-[3px] w-7 -translate-y-1/2 rounded-full bg-slate-800 transition-all duration-300',
                                isMenuOpen ? 'opacity-0' : 'opacity-100'
                            ]" />

                            <span :class="[
                                'absolute left-0 h-[3px] w-7 rounded-full bg-slate-800 transition-all duration-300',
                                isMenuOpen ? 'top-1/2 -translate-y-1/2 -rotate-45' : 'bottom-0'
                            ]" />
                        </span>
                    </button>
                </div>
            </div>
        </div>
    </header>
</template>